import React, { useCallback, useEffect, useRef, useState } from "react";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import CustomButton from "components/CustomButton";
import "./imageCropper.scss";

const ZOOM_MIN = 1;
const ZOOM_MAX = 4;
const ZOOM_STEP = 0.01;

/**
 * Pemotong gambar: geser untuk menentukan bagian yang dipakai, slider untuk
 * perbesar. Dibuat sendiri, bukan memakai paket pihak ketiga, supaya tidak
 * menambah dependensi dan ukuran bundle — mekanismenya hanya transform CSS
 * ditambah satu `drawImage` ke canvas.
 *
 * Kontrak koordinat:
 *  - `zoom` 1 berarti gambar PAS menutupi viewport (sisi terpendeknya menempel).
 *  - `offset` adalah pergeseran dalam piksel CSS dari posisi tengah, dan selalu
 *    dijepit agar viewport tidak pernah menyisakan area kosong.
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {string} props.src  object URL gambar yang dipilih pengguna
 * @param {string} [props.fileName]  nama berkas asli, dipakai untuk hasilnya
 * @param {string} [props.mimeType]  tipe berkas asli; PNG dipertahankan
 * @param {number} [props.aspect]  lebar/tinggi area potong. 1 = persegi
 * @param {number} [props.outputWidth]  lebar hasil dalam piksel
 * @param {(file: File) => void} props.onConfirm
 * @param {() => void} props.onCancel
 */
const ImageCropper = ({
  isOpen,
  src,
  fileName = "foto-profil",
  mimeType = "image/jpeg",
  aspect = 1,
  outputWidth = 512,
  onConfirm,
  onCancel,
}) => {
  const viewportRef = useRef(null);
  const imageRef = useRef(null);
  // Titik awal satu gerakan geser: posisi pointer dan offset saat itu.
  const dragRef = useRef(null);

  const [zoom, setZoom] = useState(ZOOM_MIN);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [natural, setNatural] = useState({ width: 0, height: 0 });
  const [viewport, setViewport] = useState({ width: 0, height: 0 });
  const [isSaving, setIsSaving] = useState(false);

  // Gambar baru selalu dimulai dari keadaan netral.
  useEffect(() => {
    setZoom(ZOOM_MIN);
    setOffset({ x: 0, y: 0 });
    setNatural({ width: 0, height: 0 });
  }, [src]);

  // Ukuran viewport diukur dari DOM, bukan dipatok di JS, supaya tetap benar
  // saat modalnya menyempit di layar kecil.
  const measureViewport = useCallback(() => {
    const node = viewportRef.current;
    if (!node) return;
    setViewport({ width: node.clientWidth, height: node.clientHeight });
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;
    measureViewport();
    window.addEventListener("resize", measureViewport);
    return () => window.removeEventListener("resize", measureViewport);
  }, [isOpen, measureViewport]);

  // Skala natural -> CSS pada zoom 1. `max` yang membuatnya "cover".
  const baseScale =
    natural.width && viewport.width
      ? Math.max(
          viewport.width / natural.width,
          viewport.height / natural.height
        )
      : 1;

  const scale = baseScale * zoom;
  const displayWidth = natural.width * scale;
  const displayHeight = natural.height * scale;

  // Batas geser: setengah dari kelebihan ukuran gambar terhadap viewport.
  const clampOffset = useCallback(
    (next, atScale) => {
      const dispW = natural.width * atScale;
      const dispH = natural.height * atScale;
      const maxX = Math.max(0, (dispW - viewport.width) / 2);
      const maxY = Math.max(0, (dispH - viewport.height) / 2);
      return {
        x: Math.min(maxX, Math.max(-maxX, next.x)),
        y: Math.min(maxY, Math.max(-maxY, next.y)),
      };
    },
    [natural.width, natural.height, viewport.width, viewport.height]
  );

  const handleImageLoad = (event) => {
    const { naturalWidth, naturalHeight } = event.currentTarget;
    setNatural({ width: naturalWidth, height: naturalHeight });
    measureViewport();
  };

  const handlePointerDown = (event) => {
    if (!natural.width) return;
    // setPointerCapture: gerakan tetap terlacak walau kursor keluar dari kotak.
    event.currentTarget.setPointerCapture(event.pointerId);
    dragRef.current = {
      pointerX: event.clientX,
      pointerY: event.clientY,
      startX: offset.x,
      startY: offset.y,
    };
  };

  const handlePointerMove = (event) => {
    const drag = dragRef.current;
    if (!drag) return;
    setOffset(
      clampOffset(
        {
          x: drag.startX + (event.clientX - drag.pointerX),
          y: drag.startY + (event.clientY - drag.pointerY),
        },
        scale
      )
    );
  };

  const endDrag = (event) => {
    if (!dragRef.current) return;
    dragRef.current = null;
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  // Zoom harus memindai ulang offset: memperkecil bisa membuat offset lama
  // melewati batas dan menyisakan area kosong di viewport.
  const applyZoom = (nextZoom) => {
    const clampedZoom = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, nextZoom));
    setZoom(clampedZoom);
    setOffset((prev) => clampOffset(prev, baseScale * clampedZoom));
  };

  const handleWheel = (event) => {
    if (!natural.width) return;
    event.preventDefault();
    applyZoom(zoom - event.deltaY * 0.002);
  };

  const handleReset = () => {
    setZoom(ZOOM_MIN);
    setOffset({ x: 0, y: 0 });
  };

  const handleConfirm = async () => {
    const image = imageRef.current;
    if (!image || !natural.width || !viewport.width) return;

    setIsSaving(true);
    try {
      // Titik tengah viewport dalam koordinat piksel asli gambar.
      const centerX = natural.width / 2 - offset.x / scale;
      const centerY = natural.height / 2 - offset.y / scale;
      const sourceWidth = viewport.width / scale;
      const sourceHeight = viewport.height / scale;

      const canvas = document.createElement("canvas");
      canvas.width = outputWidth;
      canvas.height = Math.round(outputWidth / aspect);

      const context = canvas.getContext("2d");
      // Latar putih supaya PNG transparan tidak jadi hitam saat disimpan JPEG.
      context.fillStyle = "#FFFFFF";
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.imageSmoothingQuality = "high";
      context.drawImage(
        image,
        centerX - sourceWidth / 2,
        centerY - sourceHeight / 2,
        sourceWidth,
        sourceHeight,
        0,
        0,
        canvas.width,
        canvas.height
      );

      const outputType = mimeType === "image/png" ? "image/png" : "image/jpeg";
      const blob = await new Promise((resolve) =>
        canvas.toBlob(resolve, outputType, 0.92)
      );

      if (!blob) return;

      const extension = outputType === "image/png" ? "png" : "jpg";
      const baseName = fileName.replace(/\.[^.]+$/, "") || "foto-profil";
      onConfirm(
        new File([blob], `${baseName}.${extension}`, { type: outputType })
      );
    } finally {
      setIsSaving(false);
    }
  };

  const canSave = Boolean(natural.width) && !isSaving;

  return (
    <Modal isOpen={isOpen} toggle={onCancel} centered size="md">
      <ModalHeader toggle={onCancel}>Sesuaikan Foto</ModalHeader>
      <ModalBody>
        <p className="cropper-hint">
          Geser gambar untuk menentukan bagian yang dipakai, lalu atur
          perbesaran. Bagian di dalam kotak inilah yang akan tampil sebagai foto
          profil.
        </p>

        <div
          ref={viewportRef}
          className="cropper-viewport"
          style={{ aspectRatio: String(aspect) }}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          onWheel={handleWheel}
        >
          {src && (
            <img
              ref={imageRef}
              src={src}
              alt="Foto yang akan dipotong"
              className="cropper-image"
              onLoad={handleImageLoad}
              draggable={false}
              style={{
                width: displayWidth ? `${displayWidth}px` : "auto",
                height: displayHeight ? `${displayHeight}px` : "auto",
                transform: `translate(calc(-50% + ${offset.x}px), calc(-50% + ${offset.y}px))`,
              }}
            />
          )}
        </div>

        <div className="cropper-zoom">
          <label htmlFor="cropper-zoom-input">Perbesaran</label>
          <input
            id="cropper-zoom-input"
            type="range"
            min={ZOOM_MIN}
            max={ZOOM_MAX}
            step={ZOOM_STEP}
            value={zoom}
            onChange={(event) => applyZoom(Number(event.target.value))}
          />
          <button type="button" className="cropper-reset" onClick={handleReset}>
            Atur Ulang
          </button>
        </div>
      </ModalBody>
      <ModalFooter>
        {/* Keduanya memakai kelas yang sama, bukan `button-filter` dan
            `primary-btn`: dua kelas itu berasal dari keluarga gaya yang
            berbeda (tombol filter toolbar vs tombol utama), sehingga tinggi
            dan paddingnya tidak pernah sama. */}
        <CustomButton
          label="Batal"
          className="cropper-btn is-secondary"
          color=""
          ukuran=""
          onClick={onCancel}
        />
        <CustomButton
          label={isSaving ? "Memproses…" : "Gunakan Foto"}
          className="cropper-btn is-primary"
          color=""
          ukuran=""
          disabled={!canSave}
          onClick={handleConfirm}
        />
      </ModalFooter>
    </Modal>
  );
};

export default ImageCropper;
